<script lang="ts" setup>
import { useAuthStore } from '../store'
import { ref, computed } from 'vue'
import { useThemeStore } from '@/stores/theme'

const themeStore = useThemeStore()
const currentTheme = computed(() => themeStore.currentTheme)
const authStore = useAuthStore()

const email = ref<string>('')
const password = ref<string>('')
const rememberMe = ref<boolean>(false)
const showPassword = ref<boolean>(false)

const isLoading = ref<boolean>(false)
const isSignInDisabled = ref<boolean>(false)

const error = ref<boolean>(false)
const errorMessages = ref<string>('')
const errorProvider = ref<boolean>(false)
const errorProviderMessages = ref<string>('')

const errorPassLogin = computed(() => authStore.errorPassLogin)
const errorPassLoginMsg = computed(() => authStore.errorPassLoginMsg)
const errorLoginMsg = computed(() => authStore.errorLoginMsg)

async function btnSubmit(): void {
  if (email.value && password.value) {
    isLoading.value = true
    isSignInDisabled.value = true
    await authStore.signIn({ email: email.value, password: password.value })
    isLoading.value = false
    isSignInDisabled.value = false
  }
}

function resetErrors(): void {
  error.value = false
  errorMessages.value = ''

  errorProvider.value = false
  errorProviderMessages.value = ''
}

function btnToggleTheme(): void {
  themeStore.toggleTheme()
}
</script>

<template>
  <v-container fluid class="fill-height pa-0">
    <v-row no-gutters class="fill-height">
      <!-- Left side with illustrations and stats -->
      <v-col cols="12" md="8" class="bg-background position-relative d-flex align-center">
        <!-- Logo in top left -->
    

        <!-- Theme Toggle Button -->
        <div class="position-absolute" style="top: 24px; right: 24px;">
          <v-btn icon variant="text" @click="btnToggleTheme">
            <v-icon>{{ currentTheme === 'myCustomDarkTheme' ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent'
              }}</v-icon>
          </v-btn>
        </div>

        <!-- Content area with character and stats -->
        <v-container class="d-flex flex-column justify-center" style="max-width: 900px;">
          <v-row no-gutters class="fill-height flex-grow-1">

            <!-- Stats cards -->
            <v-col cols="4">
              <v-card variant="flat" class="pa-8" width="100%" style="max-width: 400px;">

                <v-avatar color="primary" rounded size="44" class="mb-4">
                  <v-icon color="white">mdi-file-document-outline</v-icon>
                </v-avatar>
                <div class="text-subtitle-1 mb-1 font-weight-medium">Nuevo crecimiento</div>
                <div class="d-flex align-center">
                  <span class="text-h4 font-weight-bold">862</span>
                  <span class="text-caption font-weight-medium text-success ml-2">+22%</span>
                </div>
                <div class="text-caption mt-1">Mensual</div>
              </v-card>
            </v-col>

            <!-- Center with character illustration -->
            <v-col cols="4" class="d-flex justify-center">
              <img src="/isowipi.svg" alt="Character" height="400" />
            </v-col>

            <!-- Profit stats -->
            <v-col cols="4" class="d-flex flex-column justify-center">
              <!-- Top profit card -->
              <v-card variant="flat" class="rounded-lg pa-4 mb-4" min-height="80px">
                <div class="d-flex">
                  <div>
                    <div class="text-subtitle-1 mb-1 font-weight-medium">Nuevas racies</div>
                    <div class="d-flex align-center">
                      <span class="text-h4 font-weight-bold">344</span>
                      <v-icon color="success" size="small" class="mx-1">mdi-arrow-up</v-icon>
                      <span class="text-caption font-weight-medium text-success">18.2%</span>
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-avatar color="amber-lighten-5" size="44">
                    <v-icon color="warning">mdi-chart-line</v-icon>
                  </v-avatar>
                </div>
              </v-card>

              <!-- Bottom profit card with chart -->
              <v-card variant="flat" class="rounded-lg pa-4" min-height="100px">
                <div class="text-h5 font-weight-bold mb-2">+ 5</div>
                      <v-icon color="success" size="small" class="mx-1">mdi-image</v-icon>
                <div class="text-subtitle-1 font-weight-medium">Mediciones</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Decorative elements -->
          <div class="position-absolute" style="bottom: 16px; left: 16px;">
            <v-img src="/isowipi.svg" alt="Decoration" />
          </div>
        </v-container>
      </v-col>

      <!-- Right side login form -->
      <v-col cols="12" md="4" class="login-wrapper d-flex align-center justify-center">
        <v-card variant="flat" class="pa-8" width="100%" max-width="400">
          <!-- Logo at the top -->
         
            <v-img contain height="130px" src="/wipilogo.svg" class="mb-6" />
         
          <h1 class="text-h4 font-weight-bold mb-2">Bienvenido! 👋</h1>
          <p class="text-body-1 text-medium-emphasis mb-6">Porfavor, ingrese con su cuenta de Torcaza.</p>

          <!-- Demo credentials alert -->
          <v-sheet color="purple-lighten-5" class="rounded-lg pa-4 mb-8">
            <div class="text-body-2">
              <span class="font-weight-medium">Novedades:</span>
              <span class="text-primary font-weight-medium"> Nuevo módulo de exportación</span>

            </div>

          </v-sheet>

          <!-- Login form -->
          <v-form @submit.prevent="btnSubmit">
            <div class="mb-4">
              <label class="text-sm text-body ms-1 font-weight-medium">EMAIL</label>
              <v-text-field v-model="email" :error="error" name="email" variant="outlined"
                prepend-inner-icon="mdi-email-outline" placeholder="johndode@wipitech.com" bg-color="bg-primary"
                @change="resetErrors" />
            </div>

            <div class="mb-2">
              <label class="text-sm text-body ms-1 font-weight-medium">PASSWORD</label>
              <v-text-field v-model="password" :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'" :error="error" :error-messages="errorMessages"
                name="password" variant="outlined" prepend-inner-icon="mdi-lock-outline" placeholder="••••••"
                bg-color="bg-primary" @change="resetErrors" @click:append-inner="showPassword = !showPassword" />
            </div>

            <div class="d-flex justify-space-between align-center mb-6">

              <!-- <v-btn variant="text" color="primary" class="text-none" href="#">Olvide mi contraseña?</v-btn> -->
            </div>

            <v-alert v-if="errorLoginMsg" type="error" class="my-2">
              {{ errorLoginMsg }}
            </v-alert>

            <v-btn type="submit" color="primary" block size="large" :loading="isLoading" :disabled="isSignInDisabled"
              class="text-none mb-6" @click="btnSubmit">
              Login
            </v-btn>

            <v-alert v-if="errorPassLogin" border="right" colored-border type="error" elevation="2" class="mt-1">
              {{ errorPassLoginMsg }}
            </v-alert>

            <!-- <div class="text-center mb-6">
              <span class="text-medium-emphasis">Nuevo en la plataforma?</span>
              <v-btn variant="text" color="primary" class="text-none font-weight-medium px-1">Crear una cuenta</v-btn>
            </div> -->

  

          </v-form>


        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.position-absolute {
  position: absolute !important;
}

label {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.login-wrapper {
  background-color: var(--v-theme-surface);
  /* Soporta dark/light */
  padding: 3rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
}
</style>